import {
  Expand,
  RectangleHorizontal,
  RectangleVertical,
  Smartphone,
  Square,
  type LucideIcon,
} from 'lucide-react'
import { HelpTopic } from '@/content/help'
import type { AspectRatio, Rect, Size } from '@/core/geometry/types'

export enum OutputPresetId {
  Original = 'original',
  Square = 'square',
  Portrait = 'portrait',
  Landscape = 'landscape',
  Story = 'story',
}

export type FixedOutputPresetId =
  | OutputPresetId.Square
  | OutputPresetId.Portrait
  | OutputPresetId.Landscape
  | OutputPresetId.Story

export enum SafeZoneId {
  StoryTop = 'story-top',
  StoryBottom = 'story-bottom',
}

export interface SafeZone {
  id: SafeZoneId
  rect: Readonly<Rect>
  label: string
}

interface OutputPresetBase {
  label: string
  ratioLabel: string
  icon: LucideIcon
  helpTopic: HelpTopic
  safeZones?: readonly SafeZone[]
}

export interface FixedOutputPreset extends OutputPresetBase {
  id: FixedOutputPresetId
  ratio: AspectRatio
  targetSize: Size
}

export interface OriginalOutputPreset extends OutputPresetBase {
  id: OutputPresetId.Original
  ratio: null
  targetSize: null
}

export type OutputPreset = FixedOutputPreset | OriginalOutputPreset

export const GRID_TILE_RATIO = { w: 3, h: 4 } as const satisfies AspectRatio
export const INSTAGRAM_FEED_RATIO_RANGE = {
  tallest: { w: 4, h: 5 },
  widest: { w: 1.91, h: 1 },
} as const satisfies Readonly<Record<'tallest' | 'widest', AspectRatio>>

const STORY_TOP_SAFE_HEIGHT = 250 / 1920
const STORY_BOTTOM_SAFE_HEIGHT = 340 / 1920

export const OUTPUT_PRESETS = [
  {
    id: OutputPresetId.Original,
    label: 'Original',
    ratio: null,
    ratioLabel: 'Source shape',
    targetSize: null,
    icon: Expand,
    helpTopic: HelpTopic.FormatOriginal,
  },
  {
    id: OutputPresetId.Square,
    label: 'Square',
    ratio: { w: 1, h: 1 },
    ratioLabel: '1:1',
    targetSize: { width: 1080, height: 1080 },
    icon: Square,
    helpTopic: HelpTopic.FormatSquare,
  },
  {
    id: OutputPresetId.Portrait,
    label: 'Portrait',
    ratio: { w: 4, h: 5 },
    ratioLabel: '4:5',
    targetSize: { width: 1080, height: 1350 },
    icon: RectangleVertical,
    helpTopic: HelpTopic.FormatPortrait,
  },
  {
    id: OutputPresetId.Landscape,
    label: 'Landscape',
    ratio: { w: 1.91, h: 1 },
    ratioLabel: '1.91:1',
    targetSize: { width: 1080, height: 566 },
    icon: RectangleHorizontal,
    helpTopic: HelpTopic.FormatLandscape,
  },
  {
    id: OutputPresetId.Story,
    label: 'Story / Reel',
    ratio: { w: 9, h: 16 },
    ratioLabel: '9:16',
    targetSize: { width: 1080, height: 1920 },
    icon: Smartphone,
    helpTopic: HelpTopic.FormatStory,
    safeZones: [
      {
        id: SafeZoneId.StoryTop,
        rect: { x: 0, y: 0, width: 1, height: STORY_TOP_SAFE_HEIGHT },
        label: 'Profile name and progress bar sit here',
      },
      {
        id: SafeZoneId.StoryBottom,
        rect: { x: 0, y: 1 - STORY_BOTTOM_SAFE_HEIGHT, width: 1, height: STORY_BOTTOM_SAFE_HEIGHT },
        label: 'Reply bar and Reels UI cover this',
      },
    ],
  },
] as const satisfies readonly OutputPreset[]

export const DEFAULT_OUTPUT_PRESET = OutputPresetId.Portrait

function safeZonesOverlap(first: SafeZone, second: SafeZone): boolean {
  return first.rect.x < second.rect.x + second.rect.width
    && first.rect.x + first.rect.width > second.rect.x
    && first.rect.y < second.rect.y + second.rect.height
    && first.rect.y + first.rect.height > second.rect.y
}

export function validateOutputPresets(presets: readonly OutputPreset[] = OUTPUT_PRESETS): void {
  const seenIds = new Set<OutputPresetId>()
  const feedRatios = Object.values(INSTAGRAM_FEED_RATIO_RANGE)
  if (feedRatios.some((ratio) => !Number.isFinite(ratio.w) || !Number.isFinite(ratio.h) || ratio.w <= 0 || ratio.h <= 0)
    || INSTAGRAM_FEED_RATIO_RANGE.tallest.w / INSTAGRAM_FEED_RATIO_RANGE.tallest.h
      > INSTAGRAM_FEED_RATIO_RANGE.widest.w / INSTAGRAM_FEED_RATIO_RANGE.widest.h) {
    throw new Error('Instagram feed ratio range must contain ordered finite positive ratios')
  }

  for (const preset of presets) {
    if (!Object.values(OutputPresetId).includes(preset.id)) {
      throw new Error(`Output preset configuration is invalid: unknown id "${String(preset.id)}"`)
    }
    if (seenIds.has(preset.id)) {
      throw new Error(`Output preset configuration is invalid: duplicate id "${preset.id}"`)
    }
    seenIds.add(preset.id)

    if (preset.id === OutputPresetId.Original) {
      if (preset.ratio !== null || preset.targetSize !== null) {
        throw new Error('Original output preset must derive its ratio and target size at runtime')
      }
    } else {
      if (preset.ratio.w <= 0 || preset.ratio.h <= 0) {
        throw new Error(`Output preset "${preset.id}" must have a positive aspect ratio`)
      }
      if (preset.targetSize.width <= 0 || preset.targetSize.height <= 0) {
        throw new Error(`Output preset "${preset.id}" must have a positive target size`)
      }
      if (!Number.isInteger(preset.targetSize.width) || !Number.isInteger(preset.targetSize.height)) {
        throw new Error(`Output preset "${preset.id}" target size must use integer pixels`)
      }
    }
    if (!preset.label.trim() || !preset.ratioLabel.trim()
      || !Object.values(HelpTopic).includes(preset.helpTopic)) {
      throw new Error(`Output preset "${preset.id}" has missing display copy`)
    }
    if (!preset.icon) {
      throw new Error(`Output preset "${preset.id}" is missing its icon component`)
    }
    const zones = preset.safeZones ?? []
    if (preset.id === OutputPresetId.Story && zones.length === 0) {
      throw new Error('Story output preset must configure safe zones')
    }
    if (preset.id !== OutputPresetId.Story && zones.length > 0) {
      throw new Error(`Only the Story output preset may configure safe zones, received "${preset.id}"`)
    }
    const seenZoneIds = new Set<SafeZoneId>()
    for (const zone of zones) {
      if (!Object.values(SafeZoneId).includes(zone.id)) {
        throw new Error(`Output preset "${preset.id}" has unknown safe zone "${String(zone.id)}"`)
      }
      if (seenZoneIds.has(zone.id)) {
        throw new Error(`Output preset "${preset.id}" has duplicate safe zone "${zone.id}"`)
      }
      seenZoneIds.add(zone.id)
      const { x, y, width, height } = zone.rect
      if (![x, y, width, height].every(Number.isFinite)
        || x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > 1 || y + height > 1) {
        throw new Error(`Output preset "${preset.id}" safe zone "${zone.id}" must be inside normalized output bounds`)
      }
      if (!zone.label.trim()) {
        throw new Error(`Output preset "${preset.id}" safe zone "${zone.id}" requires a label`)
      }
    }
    for (let firstIndex = 0; firstIndex < zones.length; firstIndex += 1) {
      for (let secondIndex = firstIndex + 1; secondIndex < zones.length; secondIndex += 1) {
        const first = zones[firstIndex]
        const second = zones[secondIndex]
        if (!first || !second) throw new Error('Safe-zone validation indexes are invalid')
        if (safeZonesOverlap(first, second)) {
          throw new Error(`Output preset "${preset.id}" safe zones "${first.id}" and "${second.id}" overlap`)
        }
      }
    }
    if (preset.id === OutputPresetId.Story) {
      for (const id of Object.values(SafeZoneId)) {
        if (!seenZoneIds.has(id)) throw new Error(`Story output preset is missing safe zone "${id}"`)
      }
    }

    if (preset.id !== OutputPresetId.Original) {
      const expectedHeight = preset.targetSize.width * (preset.ratio.h / preset.ratio.w)
      if (Math.abs(expectedHeight - preset.targetSize.height) > 1) {
        throw new Error(`Output preset "${preset.id}" target size does not match ${preset.ratioLabel}`)
      }
    }
  }

  for (const id of Object.values(OutputPresetId)) {
    if (!seenIds.has(id)) {
      throw new Error(`Output preset configuration is invalid: missing id "${id}"`)
    }
  }
}

export function getOutputPreset(id: OutputPresetId.Original): OriginalOutputPreset
export function getOutputPreset(id: FixedOutputPresetId): FixedOutputPreset
export function getOutputPreset(id: OutputPresetId): OutputPreset
export function getOutputPreset(id: OutputPresetId): OutputPreset {
  const preset = OUTPUT_PRESETS.find((candidate) => candidate.id === id)
  if (!preset) {
    throw new Error(`Unknown output preset: "${id}"`)
  }
  return preset
}

export function gridVisibleRect(postSize: Size): Rect {
  if (!Number.isFinite(postSize.width) || !Number.isFinite(postSize.height)
    || postSize.width <= 0 || postSize.height <= 0) {
    throw new Error('Instagram grid area requires finite positive post dimensions')
  }
  const postRatio = postSize.width / postSize.height
  const tileRatio = GRID_TILE_RATIO.w / GRID_TILE_RATIO.h
  if (postRatio > tileRatio) {
    const width = postSize.height * tileRatio
    return { x: (postSize.width - width) / 2, y: 0, width, height: postSize.height }
  }
  const height = postSize.width / tileRatio
  return { x: 0, y: (postSize.height - height) / 2, width: postSize.width, height }
}
